import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Separator } from '../../ui/separator';
import { 
  ArrowLeft,
  Target,
  Eye,
  Heart,
  Plus,
  Trash2,
  Save,
  Brain,
  Loader2,
  Users,
  Building,
  Zap,
  Lightbulb,
  CheckCircle,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../ui/use-toast';
import axios from 'axios';

const IdentidadeOrganizacional = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validatingIA, setValidatingIA] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [iaAnalysis, setIaAnalysis] = useState(null);
  const [criteriosAvaliacao, setCriteriosAvaliacao] = useState([]);
  const [criteriosResumo, setCriteriosResumo] = useState('');
  const [criteriosGeradosEm, setCriteriosGeradosEm] = useState(null);
  
  const [formData, setFormData] = useState({
    missao: '',
    visao: '', 
    valores: [],
    culturaDesejada: [],
    comportamentosEsperados: ''
  });

  const [newValue, setNewValue] = useState({ nome: '', descricao: '' });
  const [newCultura, setNewCultura] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Tags de cultura predefinidas
  const culturasPredefinidas = [
    'Inovadora', 'Colaborativa', 'Orientada a Resultados', 'Ágil', 
    'Inclusiva', 'Sustentável', 'Cliente-Cêntrica', 'Transparente',
    'Empreendedora', 'Diversa', 'Ética', 'Flexível'
  ];

  useEffect(() => {
    fetchIdentidadeData();
  }, []);

  const fetchIdentidadeData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/profile/identidade-organizacional`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data) {
        setFormData(response.data);
        // Carregar análise IA salva se existir
        if (response.data.analise_ia) {
          setIaAnalysis(response.data.analise_ia);
        }
        // Carregar critérios de avaliação se existirem
        if (response.data.criterios_avaliacao) {
          setCriteriosAvaliacao(response.data.criterios_avaliacao);
        }
        if (response.data.criterios_resumo) {
          setCriteriosResumo(response.data.criterios_resumo);
        }
        if (response.data.criterios_gerados_em) {
          setCriteriosGeradosEm(response.data.criterios_gerados_em);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar identidade organizacional:', error);
      // Não mostrar erro se for 404 (primeira vez)
      if (error.response?.status !== 404) {
        toast({
          title: "Erro",
          description: "Erro ao carregar dados da identidade organizacional",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validações básicas
      if (!formData.missao.trim() || !formData.visao.trim()) {
        toast({
          title: "Campos Obrigatórios",
          description: "Missão e Visão são obrigatórias",
          variant: "destructive"
        });
        return;
      }

      if (formData.valores.length === 0) {
        toast({
          title: "Valores Obrigatórios", 
          description: "Adicione pelo menos um valor organizacional",
          variant: "destructive"
        });
        return;
      }

      const response = await axios.post(
        `${backendUrl}/api/profile/identidade-organizacional`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast({
        title: "Sucesso!",
        description: "Identidade organizacional salva com sucesso",
      });

      // Executar análise da IA automaticamente após salvar
      await validateWithIA();

    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar identidade organizacional",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSincronizarFitCultural = async () => {
    try {
      setSincronizando(true);
      
      const response = await axios.post(
        `${backendUrl}/api/profile/identidade/sincronizar-fitcultural`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      // Atualizar critérios no estado
      if (response.data.criterios_gerados) {
        setCriteriosAvaliacao(response.data.criterios_gerados);
      }
      if (response.data.criterios_resumo) {
        setCriteriosResumo(response.data.criterios_resumo);
      }
      setCriteriosGeradosEm(new Date().toISOString());

      toast({
        title: "✅ Sincronização Concluída!",
        description: response.data.mensagem || `${response.data.total_atualizadas} avaliações recalculadas`,
      });

      if (response.data.erros && response.data.erros.length > 0) {
        console.warn('Erros durante sincronização:', response.data.erros);
      }

    } catch (error) {
      console.error('Erro ao sincronizar:', error);
      toast({
        title: "Erro na Sincronização",
        description: error.response?.data?.detail || "Erro ao sincronizar avaliações Fit Cultural",
        variant: "destructive"
      });
    } finally {
      setSincronizando(false);
    }
  };

  const validateWithIA = async () => {
    try {
      setValidatingIA(true);
      
      const response = await axios.post(
        `${backendUrl}/api/profile/identidade/validar-ia`,
        formData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setIaAnalysis(response.data);
      
      // Salvar análise IA na identidade organizacional
      try {
        await axios.post(
          `${backendUrl}/api/profile/identidade-organizacional`,
          {
            ...formData,
            analise_ia: response.data
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
      } catch (saveError) {
        console.warn('Erro ao salvar análise IA:', saveError);
      }
      
      toast({
        title: "Análise IA Concluída",
        description: "Validação inteligente da identidade organizacional realizada e salva",
      });

    } catch (error) {
      console.error('Erro na validação IA:', error);
      toast({
        title: "Aviso",
        description: "Análise IA não disponível no momento",
        variant: "destructive"
      });
    } finally {
      setValidatingIA(false);
    }
  };

  const addValue = () => {
    if (newValue.nome.trim() && newValue.descricao.trim()) {
      setFormData(prev => ({
        ...prev,
        valores: [...prev.valores, { ...newValue, id: Date.now() }]
      }));
      setNewValue({ nome: '', descricao: '' });
      
      toast({
        title: "Valor Adicionado",
        description: `Valor "${newValue.nome}" foi adicionado com sucesso`,
      });
    }
  };

  const removeValue = (index) => {
    setFormData(prev => ({
      ...prev,
      valores: prev.valores.filter((_, i) => i !== index)
    }));
  };

  const addCultura = () => {
    if (newCultura.trim() && !formData.culturaDesejada.includes(newCultura.trim())) {
      setFormData(prev => ({
        ...prev,
        culturaDesejada: [...prev.culturaDesejada, newCultura.trim()]
      }));
      setNewCultura('');
    }
  };

  const removeCultura = (cultura) => {
    setFormData(prev => ({
      ...prev,
      culturaDesejada: prev.culturaDesejada.filter(c => c !== cultura)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Building className="w-8 h-8 text-blue-600" />
              Identidade Organizacional
            </h1>
            <p className="text-gray-600 mt-1">
              Configure a missão, visão e valores da sua empresa para análise de fit cultural com IA
            </p>
          </div>
        </div>

        {/* Análise IA removida daqui - será exibida no final */}

        {/* Missão */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Missão da Empresa
            </CardTitle>
            <CardDescription>
              Defina o propósito fundamental e a razão de existir da sua organização
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Ex: Nossa missão é transformar a logística brasileira através de soluções inovadoras..."
              value={formData.missao}
              onChange={(e) => setFormData(prev => ({ ...prev, missao: e.target.value }))}
              className="min-h-[100px] resize-none"
              maxLength={500}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {formData.missao.length}/500 caracteres
            </div>
          </CardContent>
        </Card>

        {/* Visão */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-600" />
              Visão da Empresa
            </CardTitle>
            <CardDescription>
              Descreva onde sua empresa quer chegar e como será o futuro desejado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Ex: Ser a principal referência em soluções logísticas da América Latina até 2030..."
              value={formData.visao}
              onChange={(e) => setFormData(prev => ({ ...prev, visao: e.target.value }))}
              className="min-h-[100px] resize-none"
              maxLength={500}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {formData.visao.length}/500 caracteres
            </div>
          </CardContent>
        </Card>

        {/* Valores Organizacionais */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-600" />
              Valores Organizacionais
            </CardTitle>
            <CardDescription>
              Adicione os valores fundamentais que guiam as decisões e comportamentos na empresa
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Adicionar novo valor */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="valor-nome">Nome do Valor</Label>
                <Input
                  id="valor-nome"
                  placeholder="Ex: Inovação"
                  value={newValue.nome}
                  onChange={(e) => setNewValue(prev => ({ ...prev, nome: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="valor-desc">Descrição</Label>
                <Input
                  id="valor-desc"
                  placeholder="Comportamento esperado..."
                  value={newValue.descricao}
                  onChange={(e) => setNewValue(prev => ({ ...prev, descricao: e.target.value }))}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={addValue} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </div>

            {/* Lista de valores */}
            <div className="space-y-3">
              {formData.valores.map((valor, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{valor.nome}</div>
                    <div className="text-sm text-gray-600">{valor.descricao}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeValue(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              
              {formData.valores.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  <Lightbulb className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  Nenhum valor adicionado ainda. Adicione valores para definir a cultura da empresa.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cultura Desejada */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Cultura Desejada
            </CardTitle>
            <CardDescription>
              Selecione ou adicione características que descrevem a cultura organizacional desejada
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* Tags predefinidas */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Características Predefinidas:</Label>
              <div className="flex flex-wrap gap-2">
                {culturasPredefinidas.map(cultura => (
                  <Badge
                    key={cultura}
                    variant={formData.culturaDesejada.includes(cultura) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      if (formData.culturaDesejada.includes(cultura)) {
                        removeCultura(cultura);
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          culturaDesejada: [...prev.culturaDesejada, cultura]
                        }));
                      }
                    }}
                  >
                    {cultura}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            {/* Adicionar cultura customizada */}
            <div className="flex gap-2">
              <Input
                placeholder="Adicionar característica customizada..."
                value={newCultura}
                onChange={(e) => setNewCultura(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCultura()}
              />
              <Button onClick={addCultura}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Tags selecionadas */}
            {formData.culturaDesejada.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Selecionadas:</Label>
                <div className="flex flex-wrap gap-2">
                  {formData.culturaDesejada.map(cultura => (
                    <Badge key={cultura} variant="default" className="flex items-center gap-1">
                      {cultura}
                      <button
                        onClick={() => removeCultura(cultura)}
                        className="ml-1 hover:text-red-200"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comportamentos Esperados */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-orange-600" />
              Comportamentos Esperados
            </CardTitle>
            <CardDescription>
              Descreva exemplos práticos de comportamentos que demonstram os valores na prática
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Ex: Colabora ativamente em projetos cross-funcionais, propõe soluções inovadoras, age com transparência..."
              value={formData.comportamentosEsperados}
              onChange={(e) => setFormData(prev => ({ ...prev, comportamentosEsperados: e.target.value }))}
              className="min-h-[120px] resize-none"
              maxLength={1000}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {formData.comportamentosEsperados.length}/1000 caracteres
            </div>
          </CardContent>
        </Card>

        {/* Análise IA Completa */}
        {iaAnalysis && (
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-800">
                <Brain className="w-6 h-6 text-purple-600" />
                Análise Inteligente da Identidade Organizacional
              </CardTitle>
              <CardDescription className="text-purple-700">
                Análise detalhada gerada por IA para validação e otimização da sua identidade organizacional
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Score e Resumo */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-blue-600" />
                      Resumo Executivo
                    </h4>
                    <div className="bg-white p-4 rounded-lg border text-gray-700 leading-relaxed" style={{maxHeight: 'none', overflow: 'visible'}}>
                      <div 
                        className="whitespace-pre-wrap text-sm w-full" 
                        style={{
                          maxHeight: 'none', 
                          height: 'auto', 
                          overflow: 'visible',
                          textOverflow: 'unset',
                          wordWrap: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {(iaAnalysis.summary || 'Análise completa da identidade organizacional realizada com sucesso.')}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {/* Score Card */}
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <h4 className="font-semibold text-gray-800 mb-2">Score de Qualidade</h4>
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      {Math.round((iaAnalysis.score || 0) * 100)}%
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((iaAnalysis.score || 0) * 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {iaAnalysis.score >= 0.8 ? 'Excelente' : 
                       iaAnalysis.score >= 0.6 ? 'Bom' : 
                       iaAnalysis.score >= 0.4 ? 'Regular' : 'Precisa melhorias'}
                    </p>
                  </div>
                  
                  {/* Estatísticas */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm">Estatísticas</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Missão:</span>
                        <span className="font-medium">{formData.missao.length} chars</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Visão:</span>
                        <span className="font-medium">{formData.visao.length} chars</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Valores:</span>
                        <span className="font-medium">{formData.valores.length} items</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cultura:</span>
                        <span className="font-medium">{formData.culturaDesejada.length} aspectos</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Insights e Recomendações */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Insights */}
                {iaAnalysis.insights && iaAnalysis.insights.length > 0 && (
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-yellow-500" />
                      Insights Identificados
                    </h4>
                    <ul className="space-y-2">
                      {iaAnalysis.insights.map((insight, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <div className="w-2 h-2 bg-yellow-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-gray-700">{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Recomendações */}
                {iaAnalysis.recomendacoes && iaAnalysis.recomendacoes.length > 0 && (
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Recomendações de Melhoria
                    </h4>
                    <ul className="space-y-2">
                      {iaAnalysis.recomendacoes.map((rec, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-gray-700">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Análise Detalhada por Seção */}
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-500" />
                  Análise Detalhada por Componente
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Análise da Missão */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <h5 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                      <Target className="w-3 h-3" />
                      Missão
                    </h5>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span>Clareza:</span>
                        <span className="font-medium text-blue-600">
                          {formData.missao.length > 100 ? 'Excelente' : 
                           formData.missao.length > 50 ? 'Boa' : 'Precisa expansão'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Especificidade:</span>
                        <span className="font-medium text-blue-600">
                          {formData.missao.includes('transformar') || formData.missao.includes('inovar') ? 'Alta' : 'Média'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Análise da Visão */}
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h5 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                      <Eye className="w-3 h-3" />
                      Visão
                    </h5>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span>Aspiração:</span>
                        <span className="font-medium text-green-600">
                          {formData.visao.includes('líder') || formData.visao.includes('referência') ? 'Alta' : 'Média'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Prazo definido:</span>
                        <span className="font-medium text-green-600">
                          {/\d{4}/.test(formData.visao) ? 'Sim' : 'Não especificado'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Análise dos Valores */}
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <h5 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                      <Heart className="w-3 h-3" />
                      Valores
                    </h5>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span>Quantidade:</span>
                        <span className="font-medium text-purple-600">
                          {formData.valores.length >= 3 ? 'Adequada' : 'Insuficiente'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Detalhamento:</span>
                        <span className="font-medium text-purple-600">
                          {formData.valores.every(v => v.descricao && v.descricao.length > 20) ? 'Completo' : 'Básico'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Análise da Cultura */}
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <h5 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                      <Users className="w-3 h-3" />
                      Cultura Desejada
                    </h5>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span>Diversidade:</span>
                        <span className="font-medium text-orange-600">
                          {formData.culturaDesejada.length >= 3 ? 'Rica' : 'Limitada'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Modernidade:</span>
                        <span className="font-medium text-orange-600">
                          {formData.culturaDesejada.some(c => ['ágil', 'digital', 'inovadora'].includes(c.toLowerCase())) ? 'Alta' : 'Tradicional'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Próximos Passos */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  Próximos Passos Sugeridos
                </h4>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>✅ Validar identidade organizacional com stakeholders</p>
                  <p>✅ Criar questionário de fit cultural baseado nestes valores</p>
                  <p>✅ Implementar processo de avaliação contínua</p>
                  <p>✅ Integrar com processos de recrutamento e desenvolvimento</p>
                </div>
              </div>
              
            </CardContent>
          </Card>
        )}

        {/* Alerta Informativo */}
        <Alert className="bg-purple-50 border-purple-200">
          <TrendingUp className="h-4 w-4 text-purple-600" />
          <AlertDescription className="text-sm">
            <strong>Sincronizar Fit Cultural:</strong> Use este botão sempre que atualizar a Missão, Visão ou Valores da empresa. 
            Ele recalculará automaticamente todas as avaliações já realizadas para refletir os novos critérios organizacionais.
          </AlertDescription>
        </Alert>


        {/* Card de Critérios de Avaliação Gerados pela IA */}
        {criteriosAvaliacao && criteriosAvaliacao.length > 0 && (
          <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Brain className="h-5 w-5" />
                Critérios de Avaliação Fit Cultural (Gerados por IA)
              </CardTitle>
              {criteriosGeradosEm && (
                <p className="text-xs text-gray-500 mt-1">
                  Última atualização: {new Date(criteriosGeradosEm).toLocaleString('pt-BR')}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Resumo */}
              {criteriosResumo && (
                <Alert className="bg-white/70">
                  <AlertDescription className="text-sm">
                    <strong>Resumo:</strong> {criteriosResumo}
                  </AlertDescription>
                </Alert>
              )}

              {/* Lista de Critérios */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">
                  Critérios que serão usados nas avaliações:
                </p>
                {criteriosAvaliacao.map((criterio, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-purple-200 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-purple-900">{criterio.nome}</h4>
                      <span className="text-lg font-bold text-purple-600">
                        {criterio.peso}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{criterio.descricao}</p>
                    {/* Barra de progresso visual */}
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${criterio.peso}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-xs text-green-800">
                  <strong>Transparência Total:</strong> Estes são os critérios exatos que a IA usa para calcular o IFC (Índice de Fit Cultural) de cada colaborador.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}


        {/* Ações */}
        <div className="flex gap-4 justify-end">
          <Button 
            variant="outline"
            onClick={validateWithIA}
            disabled={validatingIA || !formData.missao || !formData.visao}
          >
            {validatingIA ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Brain className="w-4 h-4 mr-2" />
            )}
            Validar com IA
          </Button>
          
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="min-w-[120px]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar
          </Button>

          <Button 
            onClick={handleSincronizarFitCultural}
            disabled={sincronizando}
            className="min-w-[200px] bg-purple-600 hover:bg-purple-700"
            title="Recalcula todas as avaliações Fit Cultural com base nos valores atualizados"
          >
            {sincronizando ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TrendingUp className="w-4 h-4 mr-2" />
            )}
            Sincronizar Fit Cultural
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IdentidadeOrganizacional;